import AnchorAPI from 'client/modules/AnchorAPI';
import { CuePoint } from 'client/modules/AnchorAPI/v3/episodes';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useFetchPodcastMetadata } from 'hooks/podcast/usePodcastMetadata';
import { useFetchSpanStatus } from '../span/useSpanStatus';

export const FETCH_AD_CUE_POINTS_KEY = 'fetchAdCuePoints';

function useFetchAdCuePoints({ webEpisodeId }: { webEpisodeId?: string }) {
  const { data: podcastMetadata } = useFetchPodcastMetadata();
  const isStudioCreator = Boolean(podcastMetadata?.isStudioCreator);
  const { isSPANUser } = useFetchSpanStatus();

  /**
   * ad cue points are only accessible to:
   * - SPAN activated or deactivated users
   * - Studios creators
   *
   * if user is not one of the above the endpoint will return a 403
   */
  const isAdCuePointsEnabled = isSPANUser || isStudioCreator;

  return useQuery(
    [FETCH_AD_CUE_POINTS_KEY, webEpisodeId],
    () => {
      return AnchorAPI.fetchAdCuePoints({ episodeId: webEpisodeId! });
    },
    { enabled: !!webEpisodeId && isAdCuePointsEnabled }
  );
}

function useUpdateAdCuePoints() {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      webEpisodeId,
      cuePoints,
    }: {
      webEpisodeId: string;
      cuePoints: CuePoint[];
    }) => {
      return AnchorAPI.saveAdCuePoints({ episodeId: webEpisodeId, cuePoints });
    },
    {
      onSuccess: (_data, variables) => {
        const { webEpisodeId } = variables;
        queryClient.invalidateQueries([FETCH_AD_CUE_POINTS_KEY, webEpisodeId]);
      },
    }
  );
}

export { useFetchAdCuePoints, useUpdateAdCuePoints };
